/**
 * Frontend i18n helper for Stimulus controllers.
 * Reads translations from Rails-generated JSON embedded in the layout
 * (<script id="js-translations">), so Rails YAML is the single source of truth.
 *
 * Usage:
 *   import { t, currentLocale, localeBcp47 } from "@/utils/i18n"
 *   t('generic_error')  // returns localized string
 */

type Translations = Record<string, string>

let cachedTranslations: Translations | null = null

/** Reads and caches translations from the DOM. */
function getTranslations(): Translations {
  if (cachedTranslations) return cachedTranslations

  const element = document.getElementById('js-translations')
  if (element) {
    try {
      cachedTranslations = JSON.parse(element.textContent || '{}')
    } catch {
      cachedTranslations = {}
    }
  } else {
    cachedTranslations = {}
  }

  return cachedTranslations!
}

/** Invalidates the cached translations. Call on Turbo navigation. */
export function invalidateTranslationCache(): void {
  cachedTranslations = null
}

/** Returns the current locale from <html lang="..."> or falls back to 'en'. */
export function currentLocale(): string {
  return document.documentElement.lang || 'en'
}

/** Maps app locale to BCP 47 tag for Intl APIs. */
const LOCALE_BCP47: Record<string, string> = { cs: 'cs-CZ', en: 'en-US', es: 'es-ES', de: 'de-DE', nl: 'nl-NL' }

export function localeBcp47(): string {
  return LOCALE_BCP47[currentLocale()] || 'en-US'
}

/**
 * Translate a key with optional interpolation.
 *
 *   t('entries_updated', { count: 5 })
 *   // => "Aktualizováno 5 záznamů" (cs) or "Updated 5 entries" (en)
 */
export function t(key: string, params: Record<string, string | number> = {}): string {
  const translations = getTranslations()
  let text = translations[key] ?? key

  for (const [param, value] of Object.entries(params)) {
    text = text.replace(`%{${param}}`, String(value))
  }

  return text
}

/** Maps app locale to route path segments (for protocol/substance URLs). */
const ROUTE_PATHS: Record<string, Record<string, string>> = {
  protocols: { cs: 'protokoly', en: 'protocols', es: 'protocolos', de: 'protokolle', nl: 'protocollen' },
  substances: { cs: 'latky', en: 'substances', es: 'sustancias', de: 'substanzen', nl: 'stoffen' },
}

export function localizedPath(resource: 'protocols' | 'substances', locale?: string): string {
  const activeLocale = locale || currentLocale()
  return ROUTE_PATHS[resource][activeLocale] || ROUTE_PATHS[resource]['en']
}
